import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { useNotifications } from '../contexts/NotificationContext';
import { useTheme } from '../contexts/ThemeContext';

const NotificationsPage = () => {
  const { isDarkMode } = useTheme();
  const { unreadCount, updateUnreadCount, decrementUnreadCount } = useNotifications();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, read, unread
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const limit = 20;

  useEffect(() => {
    loadNotifications();
  }, [filter, page]);

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const params = {
        limit,
        offset: page * limit
      };

      if (filter !== 'all') {
        params.filter = filter;
      }

      const response = await apiService.getNotifications(params);
      setNotifications(response.notifications || []);
      setTotalCount(response.pagination?.total || 0);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await apiService.markNotificationAsRead(notificationId);
      setNotifications(prev => 
        prev.map(notif => 
          notif.id === notificationId ? { ...notif, read: true } : notif
        )
      );
      // Update context state
      decrementUnreadCount();
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await apiService.markAllNotificationsAsRead();
      setNotifications(prev => prev.map(notif => ({ ...notif, read: true })));
      // Update context state
      updateUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const handleDeleteNotification = async (notificationId) => {
    try {
      await apiService.deleteNotification(notificationId);
      setNotifications(prev => prev.filter(notif => notif.id !== notificationId));
      // If it was unread, decrement counter
      const deletedNotification = notifications.find(n => n.id === notificationId);
      if (deletedNotification && !deletedNotification.read) {
        decrementUnreadCount();
      }
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  const handleNotificationClick = async (notification) => {
    // If notification is unread, mark it as read
    if (!notification.read) {
      await handleMarkAsRead(notification.id);
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'transfer_received': return '💰';
      case 'transfer_sent': return '📤';
      case 'internal_transfer': return '🔄';
      case 'fee_charged': return '💳';
      case 'account_created': return '🏦';
      case 'welcome': return '👋';
      default: return '📢';
    }
  };

  const totalPages = Math.ceil(totalCount / limit);

  return (
    <div style={{ 
      padding: 'clamp(1rem, 3vw, 1.5rem)', 
      maxWidth: '1200px', 
      margin: '0 auto' 
    }}>
      <div style={{
        backgroundColor: isDarkMode ? '#2d2d2d' : 'white',
        borderRadius: '8px',
        padding: '2rem',
        boxShadow: isDarkMode ? '0 2px 4px rgba(0,0,0,0.3)' : '0 2px 4px rgba(0,0,0,0.1)',
        border: isDarkMode ? '1px solid #404040' : 'none'
      }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '2rem',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <div>
          <h2 style={{ margin: '0 0 0.5rem 0', color: isDarkMode ? '#ffffff' : '#333' }}>
            Notifications
          </h2>
          <p style={{ margin: 0, color: isDarkMode ? '#b3b3b3' : '#666' }}>
            {totalCount} total notifications {unreadCount > 0 && `(${unreadCount} unread)`}
          </p>
        </div>
        
        {unreadCount > 0 && (
          <button
            onClick={handleMarkAllAsRead}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: isDarkMode ? '#4dabf7' : '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Mark All as Read
          </button>
        )}
      </div>

      {/* Filter Tabs */}
      <div style={{
        display: 'flex',
        gap: '1rem',
        marginBottom: '2rem',
        borderBottom: isDarkMode ? '1px solid #404040' : '1px solid #eee',
        paddingBottom: '1rem'
      }}>
        {[
          { key: 'all', label: 'All' },
          { key: 'unread', label: `Unread (${unreadCount})` },
          { key: 'read', label: 'Read' }
        ].map(({ key, label }) => (
          <button
            key={key}
            onClick={() => {
              setFilter(key);
              setPage(0);
            }}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: filter === key ? (isDarkMode ? '#4dabf7' : '#007bff') : 'transparent',
              color: filter === key ? 'white' : (isDarkMode ? '#ffffff' : '#333'),
              border: filter === key ? 'none' : (isDarkMode ? '1px solid #404040' : '1px solid #ddd'),
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Notifications List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: isDarkMode ? '#b3b3b3' : '#666' }}>
          Loading notifications...
        </div>
      ) : notifications.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: isDarkMode ? '#b3b3b3' : '#666' }}>
          {filter === 'all' ? 'No notifications yet' : `No ${filter} notifications`}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {notifications.map((notification) => (
            <div
              key={notification.id}
              onClick={() => handleNotificationClick(notification)}
              style={{
                position: 'relative',
                border: isDarkMode ? '1px solid #404040' : '1px solid #eee',
                borderRadius: '8px',
                padding: '1.5rem',
                backgroundColor: notification.read 
                  ? (isDarkMode ? '#2d2d2d' : 'white') 
                  : (isDarkMode ? '#1e2a3a' : '#f8f9ff'),
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.borderColor = isDarkMode ? '#4dabf7' : '#007bff';
                e.target.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.borderColor = isDarkMode ? '#404040' : '#eee';
                e.target.style.transform = 'translateY(0)';
              }}
            >
              {/* Delete Button - Top Right Corner */}
              <button
                onClick={(e) => {
                  e.stopPropagation(); // Prevent notification click
                  handleDeleteNotification(notification.id);
                }}
                style={{
                  position: 'absolute',
                  top: '0.75rem',
                  right: '0.75rem',
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  border: 'none',
                  backgroundColor: isDarkMode ? '#555' : '#f8f9fa',
                  color: isDarkMode ? '#b3b3b3' : '#6c757d',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#dc3545';
                  e.target.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = isDarkMode ? '#555' : '#f8f9fa';
                  e.target.style.color = isDarkMode ? '#b3b3b3' : '#6c757d';
                }}
                title="Delete notification"
              >
                ×
              </button>

              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', paddingRight: '2rem' }}>
                <span style={{ fontSize: '1.5rem', flexShrink: 0 }}>
                  {getNotificationIcon(notification.type)}
                </span>
                
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '0.5rem'
                  }}>
                    <h3 style={{
                      margin: 0,
                      fontSize: '1.1rem',
                      fontWeight: notification.read ? 'normal' : 'bold',
                      color: isDarkMode ? '#ffffff' : '#333'
                    }}>
                      {notification.title}
                    </h3>
                    
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      {!notification.read && (
                        <div style={{
                          width: '8px',
                          height: '8px',
                          backgroundColor: isDarkMode ? '#4dabf7' : '#007bff',
                          borderRadius: '50%'
                        }} />
                      )}
                      <span style={{
                        color: isDarkMode ? '#b3b3b3' : '#999',
                        fontSize: '0.8rem',
                        whiteSpace: 'nowrap'
                      }}>
                        {formatTime(notification.created_at)}
                      </span>
                    </div>
                  </div>
                  
                  <p style={{
                    margin: 0,
                    color: isDarkMode ? '#b3b3b3' : '#666',
                    lineHeight: '1.4'
                  }}>
                    {notification.message}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '1rem',
          marginTop: '2rem',
          paddingTop: '2rem',
          borderTop: isDarkMode ? '1px solid #404040' : '1px solid #eee'
        }}>
          <button
            onClick={() => setPage(Math.max(0, page - 1))}
            disabled={page === 0}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: page === 0 
                ? (isDarkMode ? '#333' : '#f5f5f5') 
                : (isDarkMode ? '#4dabf7' : '#007bff'),
              color: page === 0 
                ? (isDarkMode ? '#666' : '#999') 
                : 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: page === 0 ? 'not-allowed' : 'pointer'
            }}
          >
            Previous
          </button>
          
          <span style={{ color: isDarkMode ? '#b3b3b3' : '#666' }}>
            Page {page + 1} of {totalPages}
          </span>
          
          <button
            onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
            disabled={page >= totalPages - 1}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: page >= totalPages - 1 
                ? (isDarkMode ? '#333' : '#f5f5f5') 
                : (isDarkMode ? '#4dabf7' : '#007bff'),
              color: page >= totalPages - 1 
                ? (isDarkMode ? '#666' : '#999') 
                : 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: page >= totalPages - 1 ? 'not-allowed' : 'pointer'
            }}
          >
            Next
          </button>
        </div>
      )}
      </div>
    </div>
  );
};

export default NotificationsPage; 